/**
 * AI Dashboard Chart Default Configurations
 *
 * Provides consistent styling and defaults for charts generated by the AI API.
 * These configurations merge with AI-provided options to ensure uniform appearance.
 */

// Import existing color palettes
import { chartColorPalettes } from './chartUtils'

/**
 * Default font family used across all charts
 */
export const DEFAULT_FONT_FAMILY = 'Poppins, system-ui, sans-serif'

/**
 * Default color palette for AI charts (aligned with ug-dashboard theme)
 */
export const AI_CHART_COLORS = chartColorPalettes.primary

/**
 * Base grid configuration for consistent chart spacing
 */
export const DEFAULT_GRID = {
  left: '5%',
  right: '4%',
  top: '15%',
  bottom: '10%',
  containLabel: true,
}

/**
 * Base title configuration
 */
export const DEFAULT_TITLE = {
  textStyle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#171717', // neutral-900
    fontFamily: DEFAULT_FONT_FAMILY,
  },
  left: 'center',
  top: 20,
  show: false,
}

/**
 * Base tooltip configuration with modern styling
 */
export const DEFAULT_TOOLTIP = {
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  borderColor: '#e5e7eb', // gray-200
  borderWidth: 1,
  borderRadius: 8,
  textStyle: {
    fontFamily: DEFAULT_FONT_FAMILY,
    fontSize: 13,
    color: '#374151', // gray-700
  },
  extraCssText:
    'box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);',
  padding: [10, 15],
}

/**
 * Base legend configuration
 */
export const DEFAULT_LEGEND = {
  textStyle: {
    fontFamily: DEFAULT_FONT_FAMILY,
    fontSize: 12,
    color: '#4b5563', // gray-600
  },
  top: 10,
  left: 'center',
  padding: [5, 10],
}

/**
 * Base axis label configuration
 */
export const DEFAULT_AXIS_LABEL = {
  fontFamily: DEFAULT_FONT_FAMILY,
  fontSize: 12,
  color: '#6b7280', // gray-500
}

/**
 * Default axis line styling
 */
export const DEFAULT_AXIS_LINE = {
  lineStyle: {
    color: '#e5e7eb', // gray-200
    width: 1,
  },
}

/**
 * Default split line styling
 */
export const DEFAULT_SPLIT_LINE = {
  show: true,
  lineStyle: {
    color: '#f3f4f6', // gray-100
    type: 'dashed',
  },
}

/**
 * Merge AI chart config with defaults for bar charts
 */
export function mergeBarChartDefaults(aiConfig) {
  return {
    color: AI_CHART_COLORS,
    ...aiConfig,

    title: {
      ...DEFAULT_TITLE,
      ...aiConfig.title,
    },

    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
        shadowStyle: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
      ...DEFAULT_TOOLTIP,
      ...aiConfig.tooltip,
    },

    legend: {
      ...DEFAULT_LEGEND,
      ...aiConfig.legend,
    },

    grid: {
      ...DEFAULT_GRID,
      ...aiConfig.grid,
    },

    xAxis: aiConfig.xAxis
      ? {
          axisLabel: {
            ...DEFAULT_AXIS_LABEL,
            rotate: aiConfig.xAxis.data.length > 3 ? 45 : 0,
          },
          axisLine: DEFAULT_AXIS_LINE,
          splitLine: { show: false },
          ...aiConfig.xAxis,
        }
      : undefined,

    yAxis: aiConfig.yAxis
      ? {
          nameLocation: 'middle',
          nameGap: 50,
          axisLabel: DEFAULT_AXIS_LABEL,
          axisLine: DEFAULT_AXIS_LINE,
          splitLine: DEFAULT_SPLIT_LINE,
          ...aiConfig.yAxis,
        }
      : undefined,

    series: aiConfig.series?.map(series => ({
      barMaxWidth: 60,
      itemStyle: {
        borderRadius: [4, 4, 0, 0],
      },
      label: {
        fontFamily: DEFAULT_FONT_FAMILY,
        fontSize: 11,
      },
      ...series,
    })),
  }
}

/**
 * Merge AI chart config with defaults for line charts
 */
export function mergeLineChartDefaults(aiConfig) {
  return {
    color: AI_CHART_COLORS,
    ...aiConfig,

    title: {
      ...DEFAULT_TITLE,
      ...aiConfig.title,
      show: false, // hide title as redundant
    },

    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        label: {
          backgroundColor: '#6b7280',
        },
      },
      ...DEFAULT_TOOLTIP,
      ...aiConfig.tooltip,
    },

    legend: {
      ...DEFAULT_LEGEND,
      ...aiConfig.legend,
    },

    grid: {
      ...DEFAULT_GRID,
      ...aiConfig.grid,
    },

    xAxis: aiConfig.xAxis
      ? {
          axisLabel: DEFAULT_AXIS_LABEL,
          axisLine: DEFAULT_AXIS_LINE,
          splitLine: { show: false },
          ...aiConfig.xAxis,
        }
      : undefined,

    yAxis: aiConfig.yAxis
      ? {
          axisLabel: DEFAULT_AXIS_LABEL,
          axisLine: DEFAULT_AXIS_LINE,
          splitLine: DEFAULT_SPLIT_LINE,
          ...aiConfig.yAxis,
        }
      : undefined,

    series: aiConfig.series?.map(series => ({
      smooth: true,
      lineStyle: {
        width: 3,
      },
      symbolSize: 8,
      label: {
        fontFamily: DEFAULT_FONT_FAMILY,
        fontSize: 11,
      },
      ...series,
    })),
  }
}

/**
 * Merge AI chart config with defaults for pie/donut charts
 */
export function mergePieChartDefaults(aiConfig) {
  return {
    color: AI_CHART_COLORS,
    ...aiConfig,

    title: {
      ...DEFAULT_TITLE,
      ...aiConfig.title,
    },

    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c} ({d}%)',
      ...DEFAULT_TOOLTIP,
      ...aiConfig.tooltip,
    },

    legend: {
      ...DEFAULT_LEGEND,
      orient: 'horizontal',
      bottom: 10,
      ...aiConfig.legend,
    },

    series: aiConfig.series?.map(series => ({
      type: 'pie',
      radius: series.radius || ['40%', '70%'], // Default to donut
      center: ['50%', '50%'],
      label: {
        fontFamily: DEFAULT_FONT_FAMILY,
        fontSize: 12,
        color: '#374151',
      },
      labelLine: {
        length: 15,
        length2: 10,
      },
      emphasis: {
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: 'rgba(0, 0, 0, 0.3)',
        },
        label: {
          fontSize: 14,
          fontWeight: 'bold',
        },
      },
      ...series,
    })),
  }
}

/**
 * Merge AI chart config with defaults for scatter charts
 */
export function mergeScatterChartDefaults(aiConfig) {
  return {
    color: AI_CHART_COLORS,
    ...aiConfig,

    title: {
      ...DEFAULT_TITLE,
      ...aiConfig.title,
    },

    tooltip: {
      trigger: 'item',
      ...DEFAULT_TOOLTIP,
      ...aiConfig.tooltip,
    },

    legend: {
      ...DEFAULT_LEGEND,
      ...aiConfig.legend,
    },

    grid: {
      ...DEFAULT_GRID,
      ...aiConfig.grid,
    },

    xAxis: aiConfig.xAxis
      ? {
          axisLabel: DEFAULT_AXIS_LABEL,
          axisLine: DEFAULT_AXIS_LINE,
          splitLine: DEFAULT_SPLIT_LINE,
          ...aiConfig.xAxis,
        }
      : undefined,

    yAxis: aiConfig.yAxis
      ? {
          axisLabel: DEFAULT_AXIS_LABEL,
          axisLine: DEFAULT_AXIS_LINE,
          splitLine: DEFAULT_SPLIT_LINE,
          ...aiConfig.yAxis,
        }
      : undefined,

    series: aiConfig.series?.map(series => ({
      type: 'scatter',
      symbolSize: 10,
      label: {
        fontFamily: DEFAULT_FONT_FAMILY,
        fontSize: 11,
      },
      ...series,
    })),
  }
}

/**
 * Merge AI chart config with defaults for radar charts
 */
export function mergeRadarChartDefaults(aiConfig) {
  return {
    color: AI_CHART_COLORS,
    ...aiConfig,

    title: {
      ...DEFAULT_TITLE,
      ...aiConfig.title,
    },

    tooltip: {
      trigger: 'item',
      ...DEFAULT_TOOLTIP,
      ...aiConfig.tooltip,
    },

    legend: {
      ...DEFAULT_LEGEND,
      ...aiConfig.legend,
    },

    radar: {
      center: ['50%', '55%'],
      radius: '65%',
      axisLabel: {
        fontFamily: DEFAULT_FONT_FAMILY,
        fontSize: 10,
        color: '#6b7280',
      },
      axisName: {
        fontFamily: DEFAULT_FONT_FAMILY,
        fontSize: 12,
        color: '#374151',
      },
      splitLine: {
        lineStyle: {
          color: '#e5e7eb',
        },
      },
      splitArea: {
        show: true,
        areaStyle: {
          color: ['rgba(255, 255, 255, 0)', 'rgba(0, 0, 0, 0.02)'],
        },
      },
      ...aiConfig.radar,
    },

    series: aiConfig.series?.map(series => ({
      type: 'radar',
      areaStyle: {
        opacity: 0.3,
      },
      lineStyle: {
        width: 2,
      },
      ...series,
    })),
  }
}

/**
 * Merge AI chart config with defaults for heatmap charts
 */
export function mergeHeatmapChartDefaults(aiConfig) {
  return {
    color: AI_CHART_COLORS,
    ...aiConfig,

    title: {
      ...DEFAULT_TITLE,
      ...aiConfig.title,
    },

    tooltip: {
      trigger: 'item',
      ...DEFAULT_TOOLTIP,
      ...aiConfig.tooltip,
    },

    grid: {
      ...DEFAULT_GRID,
      ...aiConfig.grid,
    },

    xAxis: aiConfig.xAxis
      ? {
          type: 'category',
          axisLabel: DEFAULT_AXIS_LABEL,
          axisLine: DEFAULT_AXIS_LINE,
          splitLine: { show: false },
          ...aiConfig.xAxis,
        }
      : undefined,

    yAxis: aiConfig.yAxis
      ? {
          type: 'category',
          axisLabel: DEFAULT_AXIS_LABEL,
          axisLine: DEFAULT_AXIS_LINE,
          splitLine: { show: false },
          ...aiConfig.yAxis,
        }
      : undefined,

    visualMap: {
      min: 0,
      max: 100,
      calculable: true,
      orient: 'horizontal',
      left: 'center',
      bottom: '5%',
      textStyle: {
        fontFamily: DEFAULT_FONT_FAMILY,
        fontSize: 11,
      },
      inRange: {
        color: ['#dbeafe', '#3b82f6', '#1e40af'], // blue gradient
      },
      ...aiConfig.visualMap,
    },

    series: aiConfig.series?.map(series => ({
      type: 'heatmap',
      label: {
        show: true,
        fontFamily: DEFAULT_FONT_FAMILY,
        fontSize: 11,
      },
      ...series,
    })),
  }
}

/**
 * Auto-detect chart type and apply appropriate defaults
 * This is the main function to use in AiEChartsViz component
 */
export function applyChartDefaults(aiConfig) {
  // If no config or already processed, return as-is
  if (!aiConfig || aiConfig._defaultsApplied) {
    return aiConfig
  }

  // Detect chart type from series
  const chartType = detectChartType(aiConfig)

  let mergedConfig

  switch (chartType) {
    case 'bar':
      mergedConfig = mergeBarChartDefaults(aiConfig)
      break
    case 'line':
      mergedConfig = mergeLineChartDefaults(aiConfig)
      break
    case 'pie':
      mergedConfig = mergePieChartDefaults(aiConfig)
      break
    case 'scatter':
      mergedConfig = mergeScatterChartDefaults(aiConfig)
      break
    case 'radar':
      mergedConfig = mergeRadarChartDefaults(aiConfig)
      break
    case 'heatmap':
      mergedConfig = mergeHeatmapChartDefaults(aiConfig)
      break
    default:
      // For unknown types, apply basic defaults
      mergedConfig = {
        color: AI_CHART_COLORS,
        ...aiConfig,
        title: {
          ...DEFAULT_TITLE,
          ...aiConfig.title,
        },
        tooltip: {
          ...DEFAULT_TOOLTIP,
          ...aiConfig.tooltip,
        },
        legend: {
          ...DEFAULT_LEGEND,
          ...aiConfig.legend,
        },
      }
  }

  // Mark as processed
  mergedConfig._defaultsApplied = true

  return mergedConfig
}

/**
 * Detect chart type from config
 */
function detectChartType(config) {
  if (!config.series || config.series.length === 0) {
    return 'unknown'
  }

  // Check first series type
  const firstSeries = config.series[0]
  const seriesType = firstSeries.type

  // Handle special cases
  if (seriesType === 'pie' && firstSeries.radius) {
    // Could be donut chart, but we treat it as pie
    return 'pie'
  }

  return seriesType || 'unknown'
}

/**
 * Utility: Create gradient color for series
 */
export function createGradientColor(
  startColor,
  endColor,
  direction = 'vertical'
) {
  return {
    type: 'linear',
    x: direction === 'horizontal' ? 0 : 0,
    y: direction === 'horizontal' ? 0 : 0,
    x2: direction === 'horizontal' ? 1 : 0,
    y2: direction === 'horizontal' ? 0 : 1,
    colorStops: [
      { offset: 0, color: startColor },
      { offset: 1, color: endColor },
    ],
  }
}

/**
 * Utility: Format large numbers for chart labels
 */
export function formatChartNumber(value) {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`
  }
  return value.toString()
}

/**
 * Utility: Format percentage for chart labels
 */
export function formatPercentage(value, decimals = 1) {
  return `${value.toFixed(decimals)}%`
}
